// Staging rules for screenshot / image attachments on a chat message (#11382).
//
// Images are turn-scoped context, not documents: they ride the message to the
// model and are never processed into queryable blobs. Sent down the document
// upload path instead they would be stored as "unsupported" blobs that never
// become queryable, so the image paths are kept apart from it.
//
// The three entry paths (paste, drag-drop, file picker) all funnel through
// here so a screenshot behaves identically however it arrives.

import type { AttachedImageRef } from "./types";

// Matches the media types the model accepts, which is also what the named
// agents surface already allows (#9827). An image the OS calls an image but
// the model can't read (TIFF, BMP, HEIC) is deliberately not attachable: it
// would stage cleanly and then fail at the API boundary.
export const ATTACHABLE_IMAGE_TYPES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

export const IMAGE_ATTACHMENT_MAX_COUNT = 8;
export const IMAGE_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

// The count and per-image limits multiply out to 40 MB, which the send encodes
// as base64 (~53 MB of body) against a server that refuses a request body over
// 25 MB — and it refuses it before any of our code runs, so the user gets an
// opaque failure at send instead of a message naming a limit. Capping the
// combined raw size at 16 MB keeps the encoded body near 21 MB with room for
// the rest of the JSON. Only binds when several large images are combined; the
// per-image limit is what a single screenshot meets.
export const IMAGE_ATTACHMENT_MAX_TOTAL_BYTES = 16 * 1024 * 1024;

/** What a composer tells the user when a staged image could not be read for
 *  the send: one sentence, so every chat surface says the same thing. */
export const IMAGE_READ_FAILED_MESSAGE = "Could not read the attached image.";

/** One image staged in the composer, before the message is sent. */
export interface StagedImageAttachment {
  // Local-only identity, so a preview can be removed before send. Not the
  // server's attachment id — that doesn't exist until the message is sent.
  id: string;
  file: File;
  name: string;
  mediaType: string;
  // Object URL backing the composer thumbnail. The owner must revoke it when
  // the image is removed or the message is sent, or the bytes stay alive for
  // the life of the document.
  previewUrl: string;
}

/** The wire shape the send endpoint takes. */
export interface OutgoingChatImage {
  name: string;
  mediaType: string;
  dataB64: string;
}

/**
 * How many sent images keep a locally-held copy. Bounds the memory a long
 * session can accumulate; older entries fall back to fetching, which is what
 * a freshly opened conversation does anyway.
 */
export const REMEMBERED_IMAGE_LIMIT = 32;

// Attachment id -> data URL for images this tab sent. Insertion-ordered, so
// the oldest key is the first one out.
const rememberedImages = new Map<string, string>();

/** A self-contained URL for bytes we already hold. */
export function imageDataUrl(mediaType: string, dataB64: string): string {
  return `data:${mediaType};base64,${dataB64}`;
}

/**
 * Keep the bytes of images we just sent, under the ids the server gave them.
 *
 * A sent message is re-rendered from server events moments later, and that
 * rebuilt copy carries refs rather than pixels. Without this the screenshot
 * would blink out while its bytes were fetched back from the server that just
 * received them.
 */
export function rememberSentImages(
  attachmentIds: string[],
  images: OutgoingChatImage[]
): void {
  // Paired strictly by position, so anything but an exact correspondence is
  // dropped: rendering one screenshot's pixels under another's id would be
  // worse than the re-fetch this avoids.
  if (attachmentIds.length !== images.length) {
    return;
  }
  attachmentIds.forEach((attachmentId, index) => {
    const image = images[index];
    rememberedImages.set(
      attachmentId,
      imageDataUrl(image.mediaType, image.dataB64)
    );
  });
  while (rememberedImages.size > REMEMBERED_IMAGE_LIMIT) {
    const oldest = rememberedImages.keys().next();
    if (oldest.done) {
      break;
    }
    rememberedImages.delete(oldest.value);
  }
}

/** The locally-held copy of a sent image, if this tab is the one that sent it. */
export function rememberedImageUrl(attachmentId: string): string | undefined {
  return rememberedImages.get(attachmentId);
}

let nextStagedImageId = 0;

export function isAttachableImage(file: File): boolean {
  return ATTACHABLE_IMAGE_TYPES.has(file.type);
}

/**
 * Split one drop / selection into images and everything else.
 *
 * Non-image files pass straight through to `documents` untouched, including
 * types the document path will itself refuse — image support must not widen
 * the document allowlist.
 */
export function partitionDroppedFiles(files: File[]): {
  images: File[];
  documents: File[];
} {
  const images: File[] = [];
  const documents: File[] = [];
  for (const file of files) {
    (isAttachableImage(file) ? images : documents).push(file);
  }
  return { images, documents };
}

function describeBytes(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

/**
 * Add `incoming` images to the `existing` staged set, enforcing the per-message
 * count, per-image size, and combined size limits.
 *
 * Refusals are returned as user-facing strings rather than thrown: a rejected
 * image must not discard the ones that were fine, and the message has to name
 * the limit. Silence here is what sent a user off to clear their browser cache.
 */
export function stageImageAttachments(
  existing: StagedImageAttachment[],
  incoming: File[]
): { staged: StagedImageAttachment[]; errors: string[] } {
  const staged: StagedImageAttachment[] = [...existing];
  const errors: string[] = [];
  let refusedForCount = 0;
  let refusedForTotal = 0;
  // Counts what is already staged, so attaching in two drops doesn't buy a
  // second budget.
  let stagedBytes = existing.reduce(
    (total, image) => total + image.file.size,
    0
  );

  for (const file of incoming) {
    if (file.size > IMAGE_ATTACHMENT_MAX_BYTES) {
      errors.push(
        `"${file.name}" is larger than the ${describeBytes(
          IMAGE_ATTACHMENT_MAX_BYTES
        )} limit.`
      );
      continue;
    }
    if (staged.length >= IMAGE_ATTACHMENT_MAX_COUNT) {
      refusedForCount += 1;
      continue;
    }
    // `continue` rather than `break`: a large image that doesn't fit shouldn't
    // discard the smaller ones behind it that still would.
    if (stagedBytes + file.size > IMAGE_ATTACHMENT_MAX_TOTAL_BYTES) {
      refusedForTotal += 1;
      continue;
    }
    stagedBytes += file.size;
    staged.push({
      id: `staged-image-${nextStagedImageId++}`,
      file,
      name: file.name,
      mediaType: file.type,
      previewUrl: URL.createObjectURL(file),
    });
  }

  if (refusedForCount > 0) {
    errors.push(
      `You can attach up to ${IMAGE_ATTACHMENT_MAX_COUNT} images to a message.`
    );
  }

  if (refusedForTotal > 0) {
    errors.push(
      `You can attach up to ${describeBytes(
        IMAGE_ATTACHMENT_MAX_TOTAL_BYTES
      )} of images to a message.`
    );
  }

  return { staged, errors };
}

/** Free a staged image's preview. Safe to call on an id that isn't staged. */
export function releaseStagedImages(staged: StagedImageAttachment[]): void {
  for (const image of staged) {
    URL.revokeObjectURL(image.previewUrl);
  }
}

function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // readAsDataURL gives "data:<media-type>;base64,<payload>"; the endpoint
    // wants the payload alone.
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read"));
    reader.readAsDataURL(file);
  });
}

/**
 * Re-encode a sent message's images for an edited re-send (#11382).
 *
 * Editing branches the conversation, and the branched message must carry the
 * same screenshots — the server stores per-message copies, so the edit sends
 * the bytes again. They are recovered in preference order: the ref's own data
 * URL (a just-sent message), the tab's remembered copy, and finally the
 * conversation's attachment route. Rejects if any image can't be recovered —
 * the caller decides whether the edit proceeds without them.
 */
export async function outgoingImagesFromRefs(
  conversationId: string | undefined,
  refs: AttachedImageRef[]
): Promise<OutgoingChatImage[]> {
  return Promise.all(
    refs.map(async (ref) => {
      const local =
        ref.previewUrl ??
        (ref.attachmentId ? rememberedImageUrl(ref.attachmentId) : undefined);
      if (local?.startsWith("data:")) {
        return {
          name: ref.name,
          mediaType: ref.mediaType,
          dataB64: local.slice(local.indexOf(",") + 1),
        };
      }
      if (!conversationId || !ref.attachmentId) {
        throw new Error("Attached image is not recoverable");
      }
      // Deferred so the module stays import-light for the composer paths that
      // never edit.
      const { default: fpFetch } = await import("../../../services/fpFetch");
      const { BASE_API_URL_V2 } = await import("../../../constants");
      const response = await fpFetch(
        `${BASE_API_URL_V2}/api/v2/chat/conversations/${conversationId}/attachments/${ref.attachmentId}`,
        { "x-finpilot-auth": true }
      );
      if (!response?.ok) {
        throw new Error("Could not read the attached image back");
      }
      return {
        name: ref.name,
        mediaType: ref.mediaType,
        dataB64: await fileToBase64(await response.blob()),
      };
    })
  );
}

/**
 * Encode staged images for the send request.
 *
 * Done at send rather than at attach so staging stays synchronous — a user who
 * attaches five screenshots and removes four should never have paid to encode
 * the four.
 */
export async function toOutgoingImages(
  staged: StagedImageAttachment[]
): Promise<OutgoingChatImage[]> {
  return Promise.all(
    staged.map(async (image) => ({
      name: image.name,
      mediaType: image.mediaType,
      dataB64: await fileToBase64(image.file),
    }))
  );
}

/**
 * Everything the dispatch boundary owes a send: the staged images encoded, and
 * the composer's previews released.
 *
 * The composer page is the ONLY place staged images become a message — every
 * downstream consumer (the conversation components, the backends, the queued
 * replay) deals exclusively in the encoded shape. The first ship of #11382
 * broke exactly here: the page dispatched staged images while the agentic
 * handler read `.dataB64` off them, so the request carried no bytes and the
 * optimistic preview was `data:...;base64,undefined`.
 */
export async function buildChatMessageSentImages(
  staged: StagedImageAttachment[]
): Promise<OutgoingChatImage[]> {
  const images = await toOutgoingImages(staged);
  // The message owns its own base64 copy now; the composer previews would
  // otherwise keep the file bytes alive for the life of the document.
  releaseStagedImages(staged);
  return images;
}

/**
 * The optimistic bubble's view of a just-sent message's images.
 *
 * There is no attachment id until the server answers, and waiting for one
 * would blank out the screenshot the user just attached — so each ref carries
 * its own bytes as a data URL.
 */
export function attachedImageRefsFromOutgoing(
  images: OutgoingChatImage[]
): AttachedImageRef[] {
  return images.map((image) => ({
    attachmentId: "",
    mediaType: image.mediaType,
    name: image.name,
    previewUrl: imageDataUrl(image.mediaType, image.dataB64),
  }));
}
